// src/components/desk/DealSplit.jsx — what a closed deal paid, shown to the person closing it.
//
// Owner ruling 2026-08-31: agents see all agent splits; managers additionally see their own
// manager split; admins see the complete split, including all managers and the agency remainder.
// This stays one component with a visibility rule so the three views cannot drift apart.
//
// It computes with `agentDealIncome`, the same function the Stats tabs and payroll read. The
// server hands back the frozen INPUTS at close (rates, who attended, the commission) rather
// than computed money, precisely so this stays the only implementation of the four allocation
// rules. A second copy on the server would be a second answer the day either is edited.
import { agentDealIncome, managerDealIncome } from '../../lib/performance.js'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'

const fmt = (n, cur) => `${cur === 'USD' ? '$' : cur === 'EUR' ? '€' : '₾'}${Math.round(Number(n) || 0).toLocaleString('en-US')}`

// snapshot: { agent_links:[{scheduler_agent_id, commission_pct}], manager_links:[…],
//             lead_scheduler_agent_id, commission_amount, commission_currency }
// viewerAgentId: the scheduler agent id of whoever is looking.
export function DealSplit({ snapshot, viewerAgentId, role = 'agent', agentsById = {} }) {
  if (!snapshot?.agent_links?.length) return null
  const cur = snapshot.commission_currency || 'GEL'
  const gross = Number(snapshot.commission_amount) || 0
  // No commission recorded → there is nothing to divide, and inventing a row of zeros would
  // read as "you earned nothing" rather than "nobody entered a figure".
  if (!gross) return null

  // The rule wants { agent_id, commission_pct }; the wire calls it scheduler_agent_id.
  const links = snapshot.agent_links.map(l => ({
    agent_id: l.scheduler_agent_id ?? l.agent_id,
    office_id: l.office_id || null,
    office_share_percent: l.office_share_percent ?? null,
    commission_pct: Number(l.commission_pct) || 0,
  }))
  const mgrLinks = (snapshot.manager_links || []).map(l => ({
    agent_id: l.scheduler_agent_id ?? l.agent_id,
    office_id: l.office_id || null,
    office_share_percent: l.office_share_percent ?? null,
    commission_pct: Number(l.commission_pct) || 0,
  }))
  const lead = snapshot.lead_scheduler_agent_id || null

  const rows = links.map(l => ({
    id: l.agent_id,
    office_id: l.office_id,
    name: agentsById[l.agent_id]?.name || t('deal_split_agent'),
    pct: l.commission_pct,
    amount: agentDealIncome(gross, links, l.agent_id, lead),
    isMe: l.agent_id === viewerAgentId,
  }))
  const mgrRows = mgrLinks.map(l => ({
    id: l.agent_id,
    office_id: l.office_id,
    name: agentsById[l.agent_id]?.name || t('deal_split_manager'),
    pct: l.commission_pct,
    amount: managerDealIncome(gross, mgrLinks, l.agent_id),
    isMe: l.agent_id === viewerAgentId,
  }))

  // Owner ruling 2026-08-31: every closer may see how the AGENT pot divides. Management may
  // additionally see only its OWN override; admin sees the complete accounting including the
  // agency remainder and every manager. This is deliberately more specific than tab.stats.
  const adminView = role === 'admin' || role === 'general_manager'
  const managerView = role === 'manager'
  const viewerOffice = agentsById[viewerAgentId]?.office_id || null
  // ── AN UNKNOWN OFFICE IS NOT AN OFFICE (swept 2026-09-02, after the admin close bug) ────
  // A manager can have no `agents` row at all, and `agents.office_id` is nullable besides
  // (`on delete set null`), so `viewerOffice` is legitimately null. Two things went wrong
  // when it was:
  //   • `r.office_id === viewerOffice` then matched every row whose office is ALSO unknown —
  //     null === null is true. That is not a scoping rule, it is a coincidence, and it
  //     showed one set of strangers' pay to another.
  //   • whatever survived was usually nothing, and the panel `return null`ed: the manager
  //     saw NO split and no reason, the same silent-empty shape as the admin who could not
  //     close a deal because management has no roster id.
  // An unknown office now scopes to NOTHING and says so, rather than guessing in either
  // direction. Widening to every office would leak other offices' pay; staying blank would
  // repeat the bug this sweep exists to remove.
  const officeUnknown = managerView && !viewerOffice
  const visibleAgents = adminView ? rows
    : managerView ? (officeUnknown ? [] : rows.filter(r => r.office_id === viewerOffice))
    : rows
  const visibleMgrs = adminView ? mgrRows
    : managerView ? (officeUnknown ? [] : mgrRows.filter(r => r.office_id === viewerOffice))
    : []
  if (officeUnknown) {
    return (
      <div className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground">
        <Icon name="alert-triangle" size={12} /> {t('deal_split_office_unknown')}
      </div>
    )
  }
  if (!visibleAgents.length && !visibleMgrs.length) return null
  // Manager overrides are additive to agent pay and come out of the company's remainder.
  // Showing only agents + managers made a perfectly balanced split look incomplete.
  const paidToAgents = rows.reduce((sum, r) => sum + r.amount, 0)
  const paidToManagers = mgrRows.reduce((sum, r) => sum + r.amount, 0)
  const agencyAmount = gross - paidToAgents - paidToManagers
  const viewerOfficeShare = links.find(l => l.office_id === viewerOffice)?.office_share_percent
  const visibleGross = managerView && Number(viewerOfficeShare) > 0
    ? gross * Number(viewerOfficeShare) / 100 : gross
  const visibleAgencyAmount = managerView
    ? visibleGross - visibleAgents.reduce((s, r) => s + r.amount, 0) - visibleMgrs.reduce((s, r) => s + r.amount, 0)
    : agencyAmount

  const Line = ({ r, kind }) => (
    <div className={`flex items-center gap-2 px-3 py-1.5 text-sm ${r.isMe ? 'bg-emerald-500/5' : ''}`}>
      <span className={`truncate ${r.isMe ? 'font-medium' : ''}`}>{r.name}</span>
      {kind === 'mgr' && <span className="text-[10px] uppercase tracking-wide text-muted-foreground/60">{t('deal_split_manager')}</span>}
      <span className="text-xs text-muted-foreground/60">{r.pct}%</span>
      <span className="ml-auto font-medium text-emerald-600 dark:text-emerald-400">{fmt(r.amount, cur)}</span>
    </div>
  )

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-3 py-2 border-b border-border flex items-center gap-2">
        <span className="text-xs font-semibold">{t('deal_split_title_all')}</span>
        {/* The base every percentage below is taken from — a percentage whose base is not on
            screen is not checkable. */}
        <span className="ml-auto text-xs text-muted-foreground">{t('stats_gross_commission')} {fmt(visibleGross, cur)}</span>
      </div>
      <div className="divide-y divide-border/50">
        {visibleAgents.map(r => <Line key={r.id} r={r} kind="agent" />)}
        {visibleMgrs.map(r => <Line key={'m' + r.id} r={r} kind="mgr" />)}
        {(adminView || managerView) && (
          <div className="flex items-center gap-2 px-3 py-1.5 text-sm">
            <span>{t('stats_company_retained')}</span>
            <span className={`ml-auto font-medium ${visibleAgencyAmount < 0 ? 'text-destructive' : 'text-foreground'}`}>
              {fmt(visibleAgencyAmount, cur)}
            </span>
          </div>
        )}
      </div>
      {/* Attendance decides WHICH two-agent rule applies, so when it was not recorded the
          division shown is the documented default rather than a measured fact. Saying so is
          cheaper than someone later discovering the number was a guess. */}
      {links.length > 1 && !lead && (
        <div className="px-3 py-1.5 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-500/5 border-t border-border">
          {t('deal_split_no_attendance')}
        </div>
      )}
    </div>
  )
}
